import React from "react";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { propertyUtil } from "views/Pma/PropertyUtil";
import { parseBedBathsString } from "../bedBathsUtils";
import { formatAddressName, getMonthAndYearString } from "./utils";
import { PROPERTY_TYPE_OPTIONS, getPropertyId } from "../utils";
import { getRowCustomization } from "./customization";
import PropertyBar from "./PropertyBar";

function MarketSaleRowPro({
  properties,
  mlsAreaID,
  older = false,
  isSingleTable,
  agentData,
  hidePropertyTypeColumn,
  hasAllActiveProperties,
  asSingleRow = false,
  reorderHandle = null,
  deleteHandle = null,
  isDeleting = false,
  isDeleted = false,
  isAdding = false,
  isManuallyAdded = false,
  wrapRow = false,
  additionalRowClassName = "",
  trProps, // optional { ref, style, className } merged onto <tr> when wrapRow (e.g. for sortable)
  editMode = false,
  onEditPropertyClick,
  rowIndex, // table row index from parent (required when asSingleRow so edit/delete act on correct row)
  hidePropertyBar = false,
  showSpecialDisclaimers = true,
  onViewPropertyDetails,
  mailProPropertyByListingId,
  onRowHoverPreview,
  printVersion = false,
}) {
  const { propTypeAcronym, shortenStatus } = propertyUtil;
  const { listgenUserID } = agentData || {};
  const isCountryClubGate = mlsAreaID === "132";

  if (!properties || !Array.isArray(properties)) {
    return asSingleRow ? null : <tbody />;
  }

  const listMap = properties.map((list, i) => {
    const custom = getRowCustomization(
      list,
      listgenUserID,
      mlsAreaID,
      isCountryClubGate,
    );

    const {
      processedList,
      isAgentListing,
      addressSize,
      offMarketDisclaimer,
      coListingDiscliamer,
      useDaysOnMarketDash,
      useComingSoon,
    } = custom;

    const dateValue = hasAllActiveProperties
      ? processedList.listDate
      : processedList.saleDate;
    const isComingSoonStatus =
      useComingSoon ||
      processedList.status === "Coming Soon" ||
      processedList.status === "Active - Coming Soon";

    // Check if property was added (from list) or manually added or edited
    const isAddedFromList =
      processedList.isAdded === true || processedList._isAdded === true;
    const isManuallyAddedRow =
      processedList._isManuallyAdded === true || isManuallyAdded;
    const isAdded = isAddedFromList || isManuallyAddedRow;
    const isEdited =
      processedList.isEdited === true || processedList._isEdited === true;
    // When hidePropertyBar (e.g. while editing in SinglePropertyAddEditDialog), do not show property bar labels
    const hasPropertyBar =
      !hidePropertyBar &&
      (isAdded || isEdited || isDeleting || isDeleted || isAdding);

    const rowClassName = [
      processedList.status && processedList.status.toLowerCase(),
      older ? "presolds-row" : "recent-row",
      isSingleTable ? "single-table-row" : "",
      isAgentListing ? "highlight-agent-listing" : "",
      // NOTE: Do not remove this line — commented out for now, to be revisited. Green row for added/manually-added.
      // (reorderHandle || deleteHandle || editMode) && isManuallyAddedRow ? "manually-added-row" : "",
      isDeleted ? "deleted-property-row" : "",
      additionalRowClassName,
    ]
      .filter(Boolean)
      .join(" ");
    const key = getPropertyId(processedList) ?? `row-${i}`;

    // Prefer the mini table row's own photoURL over the MailPro listing-ID
    // match, which only covers rows that also happen to be in the area's
    // mailing list.
    const matchedMailProRecord =
      mailProPropertyByListingId?.get(getPropertyId(processedList)) ?? null;
    const matchedPropertyDetails = matchedMailProRecord?.propertyDetails ?? null;
    const hoverPhotoUrl =
      processedList?.photoURL ||
      matchedPropertyDetails?.photos?.[0] ||
      null;

    const safeBedBaths = processedList.bedBaths || processedList.baths || "0/0";
    const bb = parseBedBathsString(safeBedBaths);

    const hasHandleColumn = !!(reorderHandle || deleteHandle);
    const statusHandles = (
      <>
        {deleteHandle && (
          <span className="delete-handle-in-cell">{deleteHandle}</span>
        )}
        {reorderHandle && (
          <span className="reorder-handle-in-cell">{reorderHandle}</span>
        )}
      </>
    );
    const offMarketAsterisk =
      showSpecialDisclaimers &&
      (processedList.isOffMarketSaleBool === true || offMarketDisclaimer) ? (
        <strong className="off-market-asterisk">*</strong>
      ) : null;
    const coListingDisclaimerEl =
      showSpecialDisclaimers && coListingDiscliamer ? (
        <strong
          className={`extra-disclaimer-star-cl ${processedList.status}-disclaimer`}
        >
          *cl{" "}
        </strong>
      ) : null;

    const statusTdClass = [
      "status-text",
      isComingSoonStatus
        ? "coming"
        : processedList.status && processedList.status.toLowerCase(),
      reorderHandle && "with-reorder-handle",
      deleteHandle && "with-delete-handle",
    ]
      .filter(Boolean)
      .join(" ");

    const propTypeRawValue = (processedList.propertyType || "").trim();
    const propTypeMapped = PROPERTY_TYPE_OPTIONS[propTypeRawValue];
    const propTypeDisplay = propTypeMapped
      ? propTypeAcronym(propTypeMapped)
      : propTypeRawValue || "-";
    // Long labels (>12 chars, e.g. "Residential Acreage") wrap to a second
    // line in this narrow column — tighten line-height and shrink font size
    // so the wrap doesn't blow out the row height.
    const propTypeStyles =
      propTypeDisplay.length > 12
        ? "prop-type-line-height-10 prop-type-small-font"
        : "";

    // One <p>: disclaimers first, then status (Coming Soon uses line break; others use string / shortenStatus).
    const statusMainBlock = (
      <p>
        {offMarketAsterisk}
        {coListingDisclaimerEl}
        {isComingSoonStatus ? (
          <>
            Coming
            <br />
            Soon
          </>
        ) : processedList.status === "ActiveContingent" ? (
          "Contingent"
        ) : (
          shortenStatus(processedList.status, "pendingOnly", true)
        )}
      </p>
    );

    const cells = (
      <>
        {/* Status: statusHandles → one <p> with disclaimers + label */}
        <td className={statusTdClass} align="left">
          {!printVersion && onViewPropertyDetails && (
            <Tooltip title="View Property Details">
              <button
                type="button"
                className="mini-table-view-details-button"
                onClick={(event) => {
                  event.stopPropagation();
                  onViewPropertyDetails(processedList);
                }}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </button>
            </Tooltip>
          )}
          {hasHandleColumn ? (
            <div className="mini-table-status-cell-inner">
              {statusHandles}
              {statusMainBlock}
            </div>
          ) : (
            statusMainBlock
          )}
        </td>

        {/* Date Column */}
        {(() => {
          const dateDisplay = getMonthAndYearString(dateValue);
          if (!dateValue || dateValue === "-" || dateDisplay === "-") {
            return (
              <td className="market-table-sale-date-row">
                <div className="date-td">
                  <div></div>
                  <div>—</div>
                  <div></div>
                </div>
              </td>
            );
          }
          return <td className="market-table-sale-date-row">{dateDisplay}</td>;
        })()}

        {/* Property Type Column */}
        {!hidePropertyTypeColumn && (
          <td className={`prop-type-text ${propTypeStyles}`}>
            {propTypeDisplay}
          </td>
        )}

        {/* Address Column */}
        <td className="address-td">
          <p className={addressSize}>
            {formatAddressName(processedList.address)}
          </p>
        </td>

        {/* List Price Column */}
        <td className="list-price-td">
          {(processedList.status === "Coming Soon" ||
            processedList.status === "Active - Coming Soon") &&
          listgenUserID !== "109954" &&
          listgenUserID !== "110007" &&
          (!processedList.listPrice || processedList.listPrice === "-") ? (
            <div className="off-centered-dash-coming-soon">
              <p>TBD</p>
            </div>
          ) : processedList.listPrice === "-" ? (
            <div className="off-centered-dash">
              <p>—</p>
            </div>
          ) : (
            <div>
              <p>$</p>
              <p>{Number(processedList.listPrice).toLocaleString()}</p>
            </div>
          )}
        </td>

        {/* Sale Price Column */}
        <td className="sale-price-td">
          {processedList.salePrice === "-" ||
          processedList.salePrice === "" ||
          Number(processedList.salePrice) === 0 ? (
            <div className="off-centered-dash">
              <p>—</p>
            </div>
          ) : (
            <div>
              <p>$</p>
              <p>{Number(processedList.salePrice).toLocaleString()}</p>
            </div>
          )}
        </td>

        {/* Days on Market Column */}
        {processedList.isOffMarketSaleBool === true &&
        (processedList.daysOnMarket === "0" ||
          processedList.daysOnMarket === "-") ? (
          <td>
            <div className="off-centered-dash">
              <p>—</p>
            </div>
          </td>
        ) : useDaysOnMarketDash ? (
          <td>
            <div className="off-centered-dash">
              <p>—</p>
            </div>
          </td>
        ) : (
          <td>{processedList.daysOnMarket}</td>
        )}

        {/* Beds Column */}
        <td>{bb.beds}</td>

        {/* Baths Column */}
        <td>
          {bb.full}/{bb.half}
        </td>

        {/* Square Feet Column */}
        <td className="square-feet-cell">
          {processedList.squareFeet === "-" ||
          processedList.squareFeet === "0" ? (
            <p>N/A</p>
          ) : (
            <p>{Math.round(processedList.squareFeet).toLocaleString()}</p>
          )}
          {(editMode || hasPropertyBar) && (
            <PropertyBar
              editMode={editMode}
              onEditPropertyClick={onEditPropertyClick}
              property={processedList}
              index={rowIndex !== undefined ? rowIndex : i}
              isDeleted={isDeleted}
              isDeleting={isDeleting}
              isAdding={isAdding}
              isManuallyAdded={isManuallyAdded}
              isAdded={isAdded}
              isEdited={isEdited}
            />
          )}
        </td>
      </>
    );

    const isRowClickable = !printVersion && !!onViewPropertyDetails;
    const rowClickHandler = isRowClickable
      ? () => onViewPropertyDetails(processedList)
      : undefined;
    const clickableRowClassName = isRowClickable ? "mini-table-row-clickable" : "";

    // Report this row's own vertical position (measured against .both-market-tables,
    // the nearest positioned ancestor) so MiniTableProCore can place ONE shared
    // preview centered under the table while still tracking whichever row is hovered.
    // Always reports on hover (even with no photo, so a "No photo found" message can
    // show in the same panel) — reusing MUI Tooltip here proved unreliable since the
    // row already carries its own onMouseEnter/onMouseLeave for this exact purpose.
    const rowPreviewActive = !printVersion && !!onRowHoverPreview;
    const rowHoverHandlers = rowPreviewActive
      ? {
          onMouseEnter: (event) =>
            onRowHoverPreview({
              photoUrl: hoverPhotoUrl,
              top: event.currentTarget.offsetTop + event.currentTarget.offsetHeight,
            }),
          onMouseLeave: () => onRowHoverPreview(null),
        }
      : undefined;

    const rowContent = wrapRow ? (
      <tr
        key={key}
        className={[rowClassName, clickableRowClassName, trProps?.className].filter(Boolean).join(" ")}
        ref={trProps?.ref}
        style={trProps?.style}
        onClick={rowClickHandler}
        {...rowHoverHandlers}
      >
        {cells}
      </tr>
    ) : (
      <React.Fragment key={key}>{cells}</React.Fragment>
    );
    return asSingleRow ? (
      rowContent
    ) : (
      <tr
        key={key}
        className={[rowClassName, clickableRowClassName].filter(Boolean).join(" ")}
        onClick={rowClickHandler}
        {...rowHoverHandlers}
      >
        {cells}
      </tr>
    );
  });

  return asSingleRow ? listMap[0] || null : <tbody>{listMap}</tbody>;
}

export default MarketSaleRowPro;
